import { resolveStoredFileUrl } from "utils/storedFileUrl";
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  Fade,
  Icon,
  InputLabel,
  Modal,
  Tooltip,
} from "@mui/material";
import { gql, useApolloClient } from "@apollo/client";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import ModalConfirm from "components/Modals/Confirm";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import TableV2 from "components/Tables/Table-v2";
import { MessageManager } from "context";
import { MenuContext } from "context/menuContext";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
import { downloadPagoCuentaCorrienteExcel } from "../utils/pagoCuentaCorrienteExcel";

const PAGE_SIZE = 8;
const MAX_COMPROBANTE_SIZE = 5 * 1024 * 1024;
const COMPROBANTE_MIME_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
const ORDER_OPTIONS = {
  CREATED_AT: "createdAt",
  CUTOFF_DATE: "cutoffDate",
};
const HISTORIAL_COLUMNS = [
  {
    name: "fechaCreacion",
    desc: "Fecha de creación",
    width: "clamp(88px, 7vw, 110px)",
    align: "left",
  },
  { name: "informacionGeneral", desc: "Información general", align: "left" },
  { name: "banco", desc: "Banco", width: "clamp(88px, 7vw, 112px)", align: "left" },
  {
    name: "nroTransferencia",
    desc: "N° de transferencia",
    width: "clamp(120px, 10vw, 160px)",
    align: "left",
  },
  { name: "estado", desc: "Estado", width: "clamp(96px, 7vw, 112px)", align: "center" },
  { name: "clientes", desc: "Clientes", width: "clamp(76px, 6vw, 96px)", align: "center" },
  { name: "montoTotal", desc: "Monto total", width: "clamp(128px, 9vw, 144px)", align: "right" },
  { name: "fechaCorte", desc: "Fecha de corte", width: "clamp(88px, 7vw, 110px)", align: "left" },
  { name: "acciones", desc: "Acciones", width: "68px", align: "center" },
];
const formatHistoryDate = (date) => {
  const [year, month, day] = String(date || "")
    .split(/[T ]/)[0]
    .split("-");

  if (!year || !month || !day) return "-";

  return Number(year) === new Date().getFullYear()
    ? `${day}/${month}`
    : `${day}/${month}/${year}`;
};

function ExpandableText({ text }) {
  const contentRef = useRef(null);
  const content = text || "-";
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [content]);

  useEffect(() => {
    const element = contentRef.current;
    if (!element || expanded) return undefined;

    const measureOverflow = () => {
      setIsTruncated(element.scrollHeight > element.clientHeight + 1);
    };
    const animationFrame = window.requestAnimationFrame(measureOverflow);

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureOverflow);
      return () => {
        window.cancelAnimationFrame(animationFrame);
        window.removeEventListener("resize", measureOverflow);
      };
    }

    const resizeObserver = new ResizeObserver(measureOverflow);
    resizeObserver.observe(element);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [content, expanded]);

  return (
    <SoftBox component="span" sx={{ display: "block", width: "100%" }}>
      <SoftBox
        ref={contentRef}
        component="span"
        sx={{
          display: expanded ? "block" : "-webkit-box",
          width: "100%",
          whiteSpace: "normal",
          overflow: expanded ? "visible" : "hidden",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          textOverflow: "ellipsis",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: expanded ? "unset" : 2,
        }}
      >
        {content}
      </SoftBox>

      {isTruncated && (
        <SoftButton
          type="button"
          variant="text"
          color="info"
          size="small"
          aria-expanded={expanded}
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((previous) => !previous);
          }}
          sx={{ minWidth: 0, p: 0, mt: 0.25, textTransform: "none" }}
        >
          {expanded ? "Ver menos" : "Ver más"}
        </SoftButton>
      )}
    </SoftBox>
  );
}

ExpandableText.propTypes = {
  text: PropTypes.string,
};

const getComprobanteUrl = (filePath) => {
  const normalizedPath = String(filePath || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");
  return normalizedPath ? resolveStoredFileUrl(normalizedPath) : null;
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "88vh",
  width: { xs: "96%", sm: "94%", lg: "94%", xxl: "90%" },
  overflow: "hidden",
  borderRadius: 3,
};

const editStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "92%", sm: "40%" },
  borderRadius: 2,
};

const LOTE_FIELDS = gql`
  fragment PagoCuentaCorrienteLoteFields on PagoCuentaCorrienteHistorial {
    id
    fechaInicio
    fechaFin
    nroTransferencia
    banco
    informacionGeneral
    leyenda
    estado
    comprobanteFile
    cantidadClientes
    montoTotal
    createdAt
  }
`;

const GET_PAGOS_CC_HISTORIAL_PAGINADO = gql`
  ${LOTE_FIELDS}
  query pagosCuentaCorrienteHistorialPaginado(
    $idSede: Int
    $tipoCliente: TipoClienteCuentaCorriente
    $pagination: PaginationInput
    $search: String
    $order: Order
  ) {
    pagosCuentaCorrienteHistorialPaginado(
      idSede: $idSede
      tipoCliente: $tipoCliente
      pagination: $pagination
      search: $search
      order: $order
    ) {
      items {
        ...PagoCuentaCorrienteLoteFields
      }
      total
      page
      limit
      totalPages
    }
  }
`;

const GET_PAGO_CC_HISTORIAL_DETALLE = gql`
  query pagoCuentaCorrienteHistorial($idLote: Int!) {
    pagoCuentaCorrienteHistorial(idLote: $idLote) {
      id
      fechaInicio
      fechaFin
      informacionGeneral
      estado
      comprobanteFile
      cantidadClientes
      montoTotal
      createdAt
      detalles {
        idCliente
        nombre
        apellido
        dni
        saldoAntes
        montoPago
        saldoActual
      }
    }
  }
`;

const ACTUALIZAR_LOTE = gql`
  ${LOTE_FIELDS}
  mutation actualizarPagoCuentaCorrienteLote(
    $idLote: Int!
    $informacionGeneral: String
    $leyenda: String
    $banco: String
    $nroTransferencia: String
  ) {
    actualizarPagoCuentaCorrienteLote(
      idLote: $idLote
      informacionGeneral: $informacionGeneral
      leyenda: $leyenda
      banco: $banco
      nroTransferencia: $nroTransferencia
    ) {
      ...PagoCuentaCorrienteLoteFields
    }
  }
`;

const SUBIR_COMPROBANTE_LOTE = gql`
  ${LOTE_FIELDS}
  mutation subirComprobantePagoCuentaCorrienteLote(
    $idLote: Int!
    $file: Upload!
    $nroTransferencia: String
    $banco: String
  ) {
    subirComprobantePagoCuentaCorrienteLote(
      idLote: $idLote
      file: $file
      nroTransferencia: $nroTransferencia
      banco: $banco
    ) {
      ...PagoCuentaCorrienteLoteFields
    }
  }
`;

const QUITAR_COMPROBANTE_LOTE = gql`
  ${LOTE_FIELDS}
  mutation quitarComprobantePagoCuentaCorrienteLote($idLote: Int!) {
    quitarComprobantePagoCuentaCorrienteLote(idLote: $idLote) {
      ...PagoCuentaCorrienteLoteFields
    }
  }
`;

export default function ModalHistorialPagos({
  open,
  handleClose,
  idSede,
  tipoCliente = "NO_FUNCIONARIO",
  refreshKey = 0,
}) {
  const apolloClient = useApolloClient();
  const { handleSnackbar } = useContext(MessageManager);
  const { menu } = useContext(MenuContext);
  const fileInputRef = useRef(null);
  const historialRequestIdRef = useRef(0);
  const [historialPagos, setHistorialPagos] = useState([]);
  const [totalHistorialPagos, setTotalHistorialPagos] = useState(0);
  const [loadingHistorialPagos, setLoadingHistorialPagos] = useState(false);
  const [downloadingHistorialId, setDownloadingHistorialId] = useState(null);
  const [uploadingHistorialId, setUploadingHistorialId] = useState(null);
  const [removingHistorialId, setRemovingHistorialId] = useState(null);
  const [editingLote, setEditingLote] = useState(null);
  const [informacionGeneralEdit, setInformacionGeneralEdit] = useState("");
  const [leyendaEdit, setLeyendaEdit] = useState("");
  const [bancoEdit, setBancoEdit] = useState("");
  const [nroTransferenciaEdit, setNroTransferenciaEdit] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingLote, setUploadingLote] = useState(null);
  const [uploadForm, setUploadForm] = useState({ nroTransferencia: "", banco: "" });
  const [comprobanteFile, setComprobanteFile] = useState(null);
  const [comprobanteError, setComprobanteError] = useState("");
  const [comprobantePreviewUrl, setComprobantePreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [removeTargetId, setRemoveTargetId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [orderBy, setOrderBy] = useState(ORDER_OPTIONS.CREATED_AT);
  const [orderDirection, setOrderDirection] = useState("desc");

  const updateHistorialItem = (updatedItem) => {
    if (!updatedItem?.id) return;
    setHistorialPagos((prev) =>
      prev.map((item) => (Number(item.id) === Number(updatedItem.id) ? { ...item, ...updatedItem } : item))
    );
  };

  const loadHistorialPagos = async () => {
    const requestId = historialRequestIdRef.current + 1;
    historialRequestIdRef.current = requestId;
    setLoadingHistorialPagos(true);
    try {
      const { data } = await apolloClient.query({
        query: GET_PAGOS_CC_HISTORIAL_PAGINADO,
        variables: {
          idSede,
          tipoCliente,
          pagination: { page, limit: PAGE_SIZE },
          search: debouncedSearch || null,
          order: { field: orderBy, order: orderDirection.toUpperCase() },
        },
        fetchPolicy: "no-cache",
      });
      if (requestId !== historialRequestIdRef.current) return;
      const result = data?.pagosCuentaCorrienteHistorialPaginado;
      const nextTotalPages = result?.totalPages || 1;
      setHistorialPagos(result?.items || []);
      setTotalHistorialPagos(result?.total || 0);
      setTotalPages(nextTotalPages);
      if (page > nextTotalPages) setPage(nextTotalPages);
    } catch (error) {
      if (requestId !== historialRequestIdRef.current) return;
      setHistorialPagos([]);
      setTotalHistorialPagos(0);
      setTotalPages(1);
      handleSnackbar(error.message, "error");
    } finally {
      if (requestId === historialRequestIdRef.current) setLoadingHistorialPagos(false);
    }
  };

  const descargarExcelHistorial = async (idLote) => {
    setDownloadingHistorialId(idLote);
    try {
      const { data: dataHistorialDetalle } = await apolloClient.query({
        query: GET_PAGO_CC_HISTORIAL_DETALLE,
        variables: { idLote: Number(idLote) },
        fetchPolicy: "no-cache",
      });
      downloadPagoCuentaCorrienteExcel(dataHistorialDetalle?.pagoCuentaCorrienteHistorial);
    } catch (error) {
      handleSnackbar(error.message, "error");
    } finally {
      setDownloadingHistorialId(null);
    }
  };

  const openEditModal = (item) => {
    setEditingLote(item);
    setInformacionGeneralEdit(item?.informacionGeneral || "");
    setLeyendaEdit(item?.leyenda || "");
    setBancoEdit(item?.banco || "");
    setNroTransferenciaEdit(item?.nroTransferencia || "");
  };

  const closeEditModal = () => {
    if (savingEdit) return;
    setEditingLote(null);
    setInformacionGeneralEdit("");
    setLeyendaEdit("");
    setBancoEdit("");
    setNroTransferenciaEdit("");
  };

  const guardarInformacionGeneral = async () => {
    if (!editingLote?.id) return;
    setSavingEdit(true);
    try {
      const { data } = await apolloClient.mutate({
        mutation: ACTUALIZAR_LOTE,
        variables: {
          idLote: Number(editingLote.id),
          informacionGeneral: informacionGeneralEdit,
          leyenda: leyendaEdit.trim() || null,
          banco: bancoEdit.trim() || null,
          nroTransferencia: nroTransferenciaEdit.trim() || null,
        },
      });
      updateHistorialItem(data?.actualizarPagoCuentaCorrienteLote);
      setEditingLote(null);
      setInformacionGeneralEdit("");
      setLeyendaEdit("");
      setBancoEdit("");
      setNroTransferenciaEdit("");
      await loadHistorialPagos();
      handleSnackbar("Información del pago actualizada", "success");
    } catch (error) {
      handleSnackbar(error.message, "error");
    } finally {
      setSavingEdit(false);
    }
  };

  const openUploadModal = (item) => {
    setUploadingLote(item);
    setUploadForm({
      nroTransferencia: item?.nroTransferencia || "",
      banco: item?.banco || "",
    });
    setComprobanteFile(null);
    setComprobanteError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const closeUploadModal = () => {
    if (uploadingHistorialId) return;
    setPreviewOpen(false);
    setUploadingLote(null);
    setUploadForm({ nroTransferencia: "", banco: "" });
    setComprobanteFile(null);
    setComprobanteError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadFormChange = (event) => {
    const { name, value } = event.target;
    setUploadForm((prev) => ({ ...prev, [name]: value }));
  };

  const seleccionarArchivoComprobante = (file) => {
    if (!file) {
      setComprobanteFile(null);
      return;
    }
    if (file.size > MAX_COMPROBANTE_SIZE) {
      setComprobanteFile(null);
      setComprobanteError("El archivo debe pesar menos de 5MB");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (!COMPROBANTE_MIME_TYPES.includes(file.type)) {
      setComprobanteFile(null);
      setComprobanteError("El archivo debe ser un PDF, JPG, JPEG o PNG");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setComprobanteFile(file);
    setComprobanteError("");
  };

  const handleComprobanteChange = (event) => {
    seleccionarArchivoComprobante(event.target.files?.[0] || null);
  };

  const handleComprobanteDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (comprobanteFile) return;
    const files = event.dataTransfer.files;
    if (files?.length !== 1) {
      setComprobanteError("Seleccione un solo archivo");
      return;
    }
    seleccionarArchivoComprobante(files[0]);
  };

  const abrirSelectorComprobante = () => {
    if (!comprobanteFile) fileInputRef.current?.click();
  };

  const abrirPreviewComprobante = () => {
    if (comprobanteFile && comprobantePreviewUrl) setPreviewOpen(true);
  };

  const closePreviewComprobante = () => {
    setPreviewOpen(false);
  };

  const handleComprobanteAreaClick = () => {
    if (comprobanteFile) abrirPreviewComprobante();
    else abrirSelectorComprobante();
  };

  const handleComprobanteAreaKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleComprobanteAreaClick();
  };

  const quitarComprobanteSeleccionado = () => {
    setPreviewOpen(false);
    setComprobanteFile(null);
    setComprobanteError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const subirComprobante = async () => {
    const idLote = Number(uploadingLote?.id);
    if (!comprobanteFile) {
      setComprobanteError((prev) => prev || "Debe seleccionar un comprobante");
      return;
    }
    if (!idLote) return;

    setUploadingHistorialId(idLote);
    try {
      const { data } = await apolloClient.mutate({
        mutation: SUBIR_COMPROBANTE_LOTE,
        variables: {
          idLote,
          file: comprobanteFile,
          nroTransferencia: uploadForm.nroTransferencia.trim() || null,
          banco: uploadForm.banco.trim() || null,
        },
      });
      updateHistorialItem(data?.subirComprobantePagoCuentaCorrienteLote);
      setUploadingLote(null);
      setUploadForm({ nroTransferencia: "", banco: "" });
      setComprobanteFile(null);
      setComprobanteError("");
      setPreviewOpen(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadHistorialPagos();
      handleSnackbar("Comprobante adjuntado. El lote ahora está Pagado", "success");
    } catch (error) {
      handleSnackbar(error.message, "error");
    } finally {
      setUploadingHistorialId(null);
    }
  };

  const quitarComprobante = async () => {
    const idLote = Number(removeTargetId);
    if (!idLote) return;
    setRemovingHistorialId(idLote);
    try {
      const { data } = await apolloClient.mutate({
        mutation: QUITAR_COMPROBANTE_LOTE,
        variables: { idLote },
      });
      updateHistorialItem(data?.quitarComprobantePagoCuentaCorrienteLote);
      handleSnackbar("Archivo eliminado. El lote volvió a Pendiente", "success");
    } catch (error) {
      handleSnackbar(error.message, "error");
    } finally {
      setRemovingHistorialId(null);
      setRemoveTargetId(null);
    }
  };

  const verComprobante = (filePath) => {
    const url = getComprobanteUrl(filePath);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (!open) {
      historialRequestIdRef.current += 1;
      setLoadingHistorialPagos(false);
      return;
    }
    setHistorialPagos([]);
    setTotalHistorialPagos(0);
    setTotalPages(1);
    setPage(1);
    setSearchTerm("");
    setDebouncedSearch("");
    setOrderBy(ORDER_OPTIONS.CREATED_AT);
    setOrderDirection("desc");
  }, [open, idSede, tipoCliente, refreshKey]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (!open) return;
    loadHistorialPagos();
  }, [open, idSede, tipoCliente, refreshKey, page, orderBy, orderDirection, debouncedSearch]);

  useEffect(() => {
    if (!comprobanteFile) {
      setComprobantePreviewUrl(null);
      return undefined;
    }
    const previewUrl = URL.createObjectURL(comprobanteFile);
    setComprobantePreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [comprobanteFile]);

  const pageSafe = Math.min(page, totalPages);

  const orderLabel = orderBy === ORDER_OPTIONS.CREATED_AT ? "Fecha de creación" : "Fecha de corte";

  const openOrderMenu = (event) => {
    menu({
      open: event.currentTarget,
      align: "right",
      options: [
        {
          name: "Fecha de creación",
          icon: { icon: "event", color: orderBy === ORDER_OPTIONS.CREATED_AT ? "success" : "info" },
          onClick: () => {
            setOrderBy(ORDER_OPTIONS.CREATED_AT);
            setPage(1);
          },
        },
        {
          name: "Fecha de corte",
          icon: { icon: "event_available", color: orderBy === ORDER_OPTIONS.CUTOFF_DATE ? "success" : "info" },
          onClick: () => {
            setOrderBy(ORDER_OPTIONS.CUTOFF_DATE);
            setPage(1);
          },
        },
      ],
    });
  };

  const openActionsMenu = (event, item) => {
    const isUploading = uploadingHistorialId === item.id;
    const isRemoving = removingHistorialId === item.id;
    menu({
      open: event.currentTarget,
      align: "right",
      options: [
        {
          name: "Descargar Excel",
          icon: { icon: "download", color: "secondary" },
          disabled: Boolean(downloadingHistorialId),
          onClick: () => descargarExcelHistorial(item.id),
        },
        {
          name: "Editar información del pago",
          icon: { icon: "edit", color: "info" },
          disabled: isUploading || isRemoving,
          onClick: () => openEditModal(item),
        },
        {
          name: "Subir comprobante de pago",
          icon: { icon: "upload_file", color: "success" },
          hidden: Boolean(item?.comprobanteFile),
          disabled: isUploading || isRemoving,
          onClick: () => openUploadModal(item),
        },
        {
          name: "Ver archivo adjunto",
          icon: { icon: "visibility", color: "success" },
          hidden: !item?.comprobanteFile,
          onClick: () => verComprobante(item.comprobanteFile),
        },
        {
          name: "Quitar archivo adjunto",
          icon: { icon: "delete_outline", color: "error" },
          hidden: !item?.comprobanteFile,
          disabled: isRemoving || isUploading,
          onClick: () => setRemoveTargetId(item.id),
        },
      ],
    });
  };

  const historialTableRows = historialPagos.map((item) => {
    const isPagado = item?.estado === "Pagado";
    const isUploading = uploadingHistorialId === item.id;
    const isRemoving = removingHistorialId === item.id;
    const isDownloading = downloadingHistorialId === item.id;

    return {
      id: item.id,
      hasBorder: true,
      fechaCreacion: formatHistoryDate(item?.createdAt),
      informacionGeneral: (
        <SoftBox>
          <ExpandableText text={item?.informacionGeneral} />
          {item?.leyenda ? (
            <Tooltip title="Leyenda visible para el cliente" placement="top-start">
              <SoftTypography variant="caption" color="info" fontWeight="medium" display="block">
                Leyenda: {item.leyenda}
              </SoftTypography>
            </Tooltip>
          ) : null}
        </SoftBox>
      ),
      banco: (
        <Tooltip title={item?.banco || "Sin banco informado"} placement="top-start">
          <SoftBox
            component="span"
            sx={{ display: "block", maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {item?.banco || "-"}
          </SoftBox>
        </Tooltip>
      ),
      nroTransferencia: (
        <Tooltip
          title={item?.nroTransferencia || "Sin número de transferencia informado"}
          placement="top-start"
        >
          <SoftBox
            component="span"
            sx={{ display: "block", maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {item?.nroTransferencia || "-"}
          </SoftBox>
        </Tooltip>
      ),
      estado: (
        <SoftBox
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.25,
            py: 0.45,
            borderRadius: 5,
            color: isPagado ? "#166534" : "#92400E",
            backgroundColor: isPagado ? "#DCFCE7" : "#FEF3C7",
            border: `1px solid ${isPagado ? "#BBF7D0" : "#FDE68A"}`,
            fontSize: "0.75rem",
            fontWeight: 700,
          }}
        >
          {isPagado ? "Pagado" : "Pendiente"}
        </SoftBox>
      ),
      clientes: item?.cantidadClientes || 0,
      montoTotal: (
        <SoftBox component="span" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
          $ {formatMoneyPunto(item?.montoTotal || 0)}
        </SoftBox>
      ),
      fechaCorte: formatHistoryDate(item?.fechaFin),
      acciones: (
        <Tooltip title="Acciones" placement="top">
          <span>
            <SoftButton
              variant="text"
              color="dark"
              size="small"
              iconOnly
              circular
              aria-label={`Acciones del traslado ${item.id}`}
              onClick={(event) => openActionsMenu(event, item)}
              disabled={isUploading || isRemoving || isDownloading}
            >
              {isUploading || isRemoving || isDownloading ? (
                <CircularProgress size={16} />
              ) : (
                <Icon>more_vert</Icon>
              )}
            </SoftButton>
          </span>
        </Tooltip>
      ),
    };
  });

  return (
    <>
      <Modal open={open} onClose={handleClose} closeAfterTransition>
        <Fade in={open}>
          <Card sx={style}>
            <SoftBox px={3} py={2.5}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                <SoftTypography variant="h5" fontWeight="bold">
                  Traslados anteriores
                </SoftTypography>

                <SoftBox display="flex" gap={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                  <SoftTypography variant="caption" fontWeight="medium">
                    Ordenar por
                  </SoftTypography>
                  <SoftTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{ cursor: "pointer", color: "primary", lineHeight: 1.2 }}
                    onClick={openOrderMenu}
                  >
                    {orderLabel}
                  </SoftTypography>

                  <Tooltip title={orderDirection === "asc" ? "Orden ascendente" : "Orden descendente"} placement="top">
                    <Icon
                      onClick={() => {
                        setOrderDirection((prev) => (prev === "asc" ? "desc" : "asc"));
                        setPage(1);
                      }}
                      sx={{ cursor: "pointer", color: "#334E72" }}
                    >
                      {orderDirection === "asc" ? "arrow_upward" : "arrow_downward"}
                    </Icon>
                  </Tooltip>

                  <SoftButton ml={4} variant="outlined" color="info" size="small" onClick={loadHistorialPagos}>
                    <Icon sx={{ mr: 0.5 }}>refresh</Icon>
                    Actualizar
                  </SoftButton>

                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer", color: "#334E72" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>

            <SoftBox p={3} sx={{ maxHeight: "calc(88vh - 88px)", overflowY: "auto" }}>
              <SoftBox mb={2} width={{ xs: "100%", md: 520 }}>
                <InputLabel htmlFor="buscarHistorialPagos">
                  <SoftTypography variant="caption" fontWeight="bold">
                    Buscar traslados
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="buscarHistorialPagos"
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Información general, banco o N° de transferencia"
                  icon={{ component: "search", direction: "left" }}
                  fullWidth
                />
              </SoftBox>

              {loadingHistorialPagos ? (
                <SoftBox sx={{ minHeight: "36vh", borderRadius: 2, border: "1px solid #E1E8F0" }}>
                  <Loading />
                </SoftBox>
              ) : historialPagos.length === 0 ? (
                <SoftBox sx={{ minHeight: "36vh", borderRadius: 2, border: "1px solid #E1E8F0", p: 2 }}>
                  <SinDatos
                    titulo={debouncedSearch ? "Sin resultados" : "Sin traslados anteriores"}
                    mensaje={
                      debouncedSearch
                        ? "No se encontraron coincidencias en información general, banco o número de transferencia."
                        : "Todavía no hay traslados registrados."
                    }
                  />
                </SoftBox>
              ) : (
                <SoftBox p={2} sx={{ border: "1px solid #DCE6F2", borderRadius: 2 }}>
                  <SoftBox sx={{ overflowX: "auto" }}>
                    <SoftBox
                      sx={{
                        width: "100%",
                        minWidth: { xs: 1050, lg: "100%" },
                        "& table": { width: "100%", tableLayout: "fixed" },
                        "& table th, & table td": { px: 1 },
                      }}
                    >
                      <TableV2 columns={HISTORIAL_COLUMNS} rows={historialTableRows} />
                    </SoftBox>
                  </SoftBox>

                  <SoftBox mt={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <SoftTypography variant="caption" color="text">
                      Mostrando {historialPagos.length} de {totalHistorialPagos} traslados
                    </SoftTypography>
                    <SoftBox display="flex" gap={1} alignItems="center">
                      <SoftButton color="primary" size="small" iconOnly circular onClick={() => setPage((prev) => Math.max(prev - 1, 1))} disabled={pageSafe <= 1}>
                        <Icon>navigate_before</Icon>
                      </SoftButton>
                      <SoftTypography variant="button" fontWeight="medium">
                        Página {pageSafe} de {totalPages}
                      </SoftTypography>
                      <SoftButton color="primary" size="small" iconOnly circular onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))} disabled={pageSafe >= totalPages}>
                        <Icon>navigate_next</Icon>
                      </SoftButton>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              )}
            </SoftBox>
          </Card>
        </Fade>
      </Modal>

      <Modal
        aria-labelledby="subir-comprobante-title"
        open={Boolean(uploadingLote)}
        onClose={closeUploadModal}
        closeAfterTransition
      >
        <Fade in={Boolean(uploadingLote)}>
          <Card sx={{ ...editStyle, maxHeight: "90vh", overflowY: "auto" }}>
            <SoftBox p={3}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <SoftTypography id="subir-comprobante-title" variant="h6" fontWeight="bold">
                  Subir comprobante de pago
                </SoftTypography>
                <Tooltip title="Cerrar" placement="top">
                  <span>
                    <Icon
                      fontSize="medium"
                      onClick={closeUploadModal}
                      sx={{ cursor: uploadingHistorialId ? "default" : "pointer", color: "#334E72" }}
                    >
                      close
                    </Icon>
                  </span>
                </Tooltip>
              </SoftBox>

              <SoftBox mt={2.5}>
                <InputLabel htmlFor="nroTransferenciaLote">
                  <SoftTypography variant="caption" fontWeight="bold">
                    Nro. de transferencia (opcional)
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="nroTransferenciaLote"
                  name="nroTransferencia"
                  type="text"
                  value={uploadForm.nroTransferencia}
                  onChange={handleUploadFormChange}
                  placeholder="Ej.: TRX-10025"
                  fullWidth
                  autoFocus
                />
              </SoftBox>

              <SoftBox mt={2}>
                <InputLabel htmlFor="bancoLote">
                  <SoftTypography variant="caption" fontWeight="bold">
                    Banco (opcional)
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="bancoLote"
                  name="banco"
                  type="text"
                  value={uploadForm.banco}
                  onChange={handleUploadFormChange}
                  placeholder="Ej.: Banco Nación"
                  fullWidth
                />
              </SoftBox>

              <SoftBox mt={2}>
                <InputLabel htmlFor="comprobanteFileLote">
                  <SoftTypography variant="caption" fontWeight="bold">
                    Comprobante de pago
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftBox
                  role="button"
                  tabIndex={0}
                  aria-label={
                    comprobanteFile
                      ? `Vista previa del comprobante seleccionado: ${comprobanteFile.name}`
                      : "Seleccionar o arrastrar comprobante de pago"
                  }
                  aria-invalid={Boolean(comprobanteError)}
                  aria-describedby={comprobanteError ? "comprobanteFileLote-error" : undefined}
                  sx={{
                    mt: 0.75,
                    border: comprobanteFile ? "2px solid #bdbdbd" : "2px dashed #bdbdbd",
                    borderRadius: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: "#fafafa",
                    transition: "border-color 0.2s",
                    height: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    "&:hover": { borderColor: getUproColor("primary") },
                    "&:focus-visible": {
                      outline: `3px solid ${getUproColor("primary")}`,
                      outlineOffset: 2,
                    },
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onDrop={handleComprobanteDrop}
                  onClick={handleComprobanteAreaClick}
                  onKeyDown={handleComprobanteAreaKeyDown}
                >
                  {!comprobanteFile ? (
                    <SoftBox width="100%" px={2}>
                      <Icon sx={{ fontSize: 40, color: getUproColor("primary") }}>cloud_upload</Icon>
                      <SoftTypography variant="body2" color="textSecondary">
                        Hacé click para seleccionar el comprobante (PDF, JPG, PNG) o arrastrá el
                        archivo aquí
                      </SoftTypography>
                    </SoftBox>
                  ) : (
                    <SoftBox
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {comprobanteFile.type.startsWith("image/") ? (
                        <img
                          src={comprobantePreviewUrl}
                          alt={`Vista previa de ${comprobanteFile.name}`}
                          style={{
                            width: "auto",
                            height: "90%",
                            maxWidth: "100%",
                            objectFit: "contain",
                            borderRadius: 8,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          }}
                        />
                      ) : comprobanteFile.type === "application/pdf" ? (
                        <SoftBox
                          component="iframe"
                          src={comprobantePreviewUrl}
                          title={`Vista previa de ${comprobanteFile.name}`}
                          width="100%"
                          height="100%"
                          sx={{ border: 0, pointerEvents: "none" }}
                        />
                      ) : (
                        <SoftTypography variant="button">
                          El archivo seleccionado no admite vista previa en línea.
                        </SoftTypography>
                      )}
                    </SoftBox>
                  )}
                </SoftBox>

                <input
                  id="comprobanteFileLote"
                  ref={fileInputRef}
                  name="comprobanteFile"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  required
                  style={{ display: "none" }}
                  onChange={handleComprobanteChange}
                  disabled={Boolean(comprobanteFile) || Boolean(uploadingHistorialId)}
                />

                {comprobanteFile && (
                  <SoftBox mt={1} display="flex" flexDirection="column" gap={1}>
                    <SoftTypography
                      variant="caption"
                      color="primary"
                      sx={{ wordBreak: "break-all", textAlign: "center", width: "100%" }}
                    >
                      {comprobanteFile.name}
                    </SoftTypography>
                    <SoftButton
                      color="error"
                      size="small"
                      variant="outlined"
                      disabled={Boolean(uploadingHistorialId)}
                      onClick={quitarComprobanteSeleccionado}
                      sx={{ width: "100%" }}
                    >
                      <Icon sx={{ fontSize: 18, mr: 1 }}>delete</Icon>
                      Quitar archivo
                    </SoftButton>
                  </SoftBox>
                )}

                <SoftButton
                  color="primary"
                  circular
                  sx={{ width: "100%", mt: 1 }}
                  disabled={Boolean(uploadingHistorialId) || Boolean(comprobanteFile)}
                  onClick={abrirSelectorComprobante}
                >
                  <Icon sx={{ mr: 1 }}>upload_file</Icon>
                  Seleccionar archivo
                </SoftButton>

                {comprobanteError && (
                  <SoftTypography
                    id="comprobanteFileLote-error"
                    mt={0.75}
                    variant="caption"
                    color="error"
                  >
                    {comprobanteError}
                  </SoftTypography>
                )}
              </SoftBox>

              <SoftBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
                <SoftButton
                  variant="outlined"
                  color="dark"
                  onClick={closeUploadModal}
                  disabled={Boolean(uploadingHistorialId)}
                >
                  Cancelar
                </SoftButton>
                <SoftButton
                  color="success"
                  onClick={subirComprobante}
                  disabled={Boolean(uploadingHistorialId)}
                >
                  {uploadingHistorialId ? (
                    <CircularProgress size={18} sx={{ mr: 1 }} />
                  ) : (
                    <Icon sx={{ mr: 0.5 }}>upload_file</Icon>
                  )}
                  Subir comprobante
                </SoftButton>
              </SoftBox>
            </SoftBox>
          </Card>
        </Fade>
      </Modal>

      <Dialog
        open={previewOpen}
        onClose={closePreviewComprobante}
        aria-labelledby="comprobante-preview-title"
        fullWidth
        maxWidth="lg"
      >
        <DialogContent sx={{ p: 2 }}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
            <SoftTypography
              id="comprobante-preview-title"
              variant="h5"
              fontWeight="bold"
              sx={{ overflowWrap: "anywhere" }}
            >
              {comprobanteFile?.name || "Comprobante de pago"}
            </SoftTypography>
            <SoftButton
              color="white"
              circular
              iconOnly
              aria-label="Cerrar vista previa"
              onClick={closePreviewComprobante}
            >
              <Icon>close</Icon>
            </SoftButton>
          </SoftBox>

          {comprobanteFile?.type.startsWith("image/") ? (
            <SoftBox
              component="img"
              src={comprobantePreviewUrl}
              alt={comprobanteFile.name}
              width="100%"
              sx={{ borderRadius: "12px" }}
            />
          ) : comprobanteFile?.type === "application/pdf" ? (
            <SoftBox
              component="iframe"
              src={comprobantePreviewUrl}
              title={comprobanteFile.name}
              width="100%"
              height="680px"
              sx={{ border: 0, borderRadius: "12px" }}
            />
          ) : (
            <SoftTypography variant="button">
              El archivo seleccionado no admite vista previa en línea.
            </SoftTypography>
          )}
        </DialogContent>
      </Dialog>

      <Modal open={Boolean(editingLote)} onClose={closeEditModal} closeAfterTransition>
        <Fade in={Boolean(editingLote)}>
          <Card sx={editStyle}>
            <SoftBox p={3}>
              <SoftTypography variant="h6" fontWeight="bold" mb={2}>
                Editar información del pago
              </SoftTypography>
              <InputLabel htmlFor="informacionGeneralLote">
                <SoftTypography variant="caption" fontWeight="bold">
                  Información general
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="informacionGeneralLote"
                value={informacionGeneralEdit}
                onChange={(event) => setInformacionGeneralEdit(event.target.value)}
                multiline
                minRows={4}
                fullWidth
                autoFocus
              />

              <SoftBox mt={2}>
                <InputLabel htmlFor="leyendaLoteEdit">
                  <SoftTypography variant="caption" fontWeight="bold">
                    Leyenda para el cliente (opcional)
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="leyendaLoteEdit"
                  name="leyenda"
                  type="text"
                  value={leyendaEdit}
                  onChange={(event) => setLeyendaEdit(event.target.value)}
                  inputProps={{ maxLength: 255 }}
                  placeholder="Si queda vacío, el cliente verá &quot;Transferencia de deuda a UPrO&quot;"
                  fullWidth
                />
              </SoftBox>

              <SoftBox
                mt={2}
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }}
                gap={2}
              >
                <SoftBox>
                  <InputLabel htmlFor="bancoLoteEdit">
                    <SoftTypography variant="caption" fontWeight="bold">
                      Banco (opcional)
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    id="bancoLoteEdit"
                    name="banco"
                    type="text"
                    value={bancoEdit}
                    onChange={(event) => setBancoEdit(event.target.value)}
                    placeholder="Ej.: Banco Nación"
                    fullWidth
                  />
                </SoftBox>

                <SoftBox>
                  <InputLabel htmlFor="nroTransferenciaLoteEdit">
                    <SoftTypography variant="caption" fontWeight="bold">
                      N° de transferencia (opcional)
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    id="nroTransferenciaLoteEdit"
                    name="nroTransferencia"
                    type="text"
                    value={nroTransferenciaEdit}
                    onChange={(event) => setNroTransferenciaEdit(event.target.value)}
                    placeholder="Ej.: TRX-10025"
                    fullWidth
                  />
                </SoftBox>
              </SoftBox>

              <SoftBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
                <SoftButton variant="outlined" color="dark" onClick={closeEditModal} disabled={savingEdit}>
                  Cancelar
                </SoftButton>
                <SoftButton color="info" onClick={guardarInformacionGeneral} disabled={savingEdit}>
                  {savingEdit ? <CircularProgress size={18} sx={{ mr: 1 }} /> : <Icon sx={{ mr: 0.5 }}>save</Icon>}
                  Guardar
                </SoftButton>
              </SoftBox>
            </SoftBox>
          </Card>
        </Fade>
      </Modal>

      <ModalConfirm
        open={Boolean(removeTargetId)}
        handleClose={() => setRemoveTargetId(null)}
        message="¿Está seguro que desea quitar el archivo adjunto? Al eliminar el comprobante, el estado del lote pasará a Pendiente."
        func={quitarComprobante}
      />
    </>
  );
}

ModalHistorialPagos.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idSede: PropTypes.number,
  tipoCliente: PropTypes.string,
  refreshKey: PropTypes.number,
};
